import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

export default function gsapDataTrigger() {
    const animationPresets = {
        "fade-up": {
            autoAlpha: 0,
            y: "1.5rem",
        },

        "fade-down": {
            autoAlpha: 0,
            y: "-1.5rem",
        },

        "fade-left": {
            autoAlpha: 0,
            x: "1.5rem",
        },

        "fade-right": {
            autoAlpha: 0,
            x: "-1.5rem",
        },

        "fade-in": {
            autoAlpha: 0,
        },

        "scale-up": {
            autoAlpha: 0,
            scale: 0.95,
        },

        "scale-down": {
            autoAlpha: 0,
            scale: 1.05,
        },

        "rotate-left": {
            autoAlpha: 0,
            rotation: -15,
        },

        "rotate-right": {
            autoAlpha: 0,
            rotation: 15,
        },

        "blur-in": {
            autoAlpha: 0,
            filter: "blur(2px)",
        },
    };

    const animatedEls = document.querySelectorAll("[data-sa-animate]");
    if (!animatedEls.length) return;

    // Default settings
    const defaultSettings = {
        duration: 1.25,
        ease: "power2.inOut",
        overwrite: "auto",
        scrollTrigger: {
            start: "top bottom",
            end: "bottom top",
            toggleActions: "play none none none",
        },
    };

    const defaultTween = "from";

    // Utility function to parse animation properties
    function parseAnimationProps(el) {
        const animateData = el.dataset.saAnimate || "";
        const props = {};

        // Split the string into individual "key: value" pairs (or preset shorthands)
        const items = animateData.split(",");

        items.forEach((item) => {
            const trimmedItem = item.trim();
            if (!trimmedItem) return;

            // Shorthand preset (no colon), e.g. "fade-up" - merge its props in, overridable by later items
            if (!trimmedItem.includes(":")) {
                if (animationPresets[trimmedItem]) {
                    Object.assign(props, animationPresets[trimmedItem]);
                }
                return;
            }

            // Split each pair into a key and a value
            const keyValuePairs = trimmedItem.split(":");

            // Make sure we have both a key and a value
            if (keyValuePairs.length === 2) {
                const key = keyValuePairs[0].trim();
                let value = keyValuePairs[1].trim();

                // Convert numeric values to actual numbers
                if (!isNaN(value)) {
                    value = parseFloat(value);
                }

                // Add the key and value to the props object
                props[key] = value;
            }
        });

        return props;
    }

    // Initialize animations based on data-sa attributes
    function initGsapAnimations() {
        animatedEls.forEach((el) => {
            // Animation properties
            const animationProps = {
                // Merge defaultSettings and user-added props
                ...defaultSettings,
                ...parseAnimationProps(el),
                // Overwrite any specific defaults with custom data attributes
                duration: parseFloat(el.dataset.saDuration) || defaultSettings.duration,
                delay: parseFloat(el.dataset.saDelay) || defaultSettings.delay,
                ease: el.dataset.saEase || defaultSettings.ease,
            };

            // console.log(animationProps);

            // ScrollTrigger properties
            const scrollTriggerProps = {
                // Merge default settings with any specific overwrites from user data attributes
                ...defaultSettings.scrollTrigger,
                start: el.dataset.saStart || defaultSettings.scrollTrigger.start,
                end: el.dataset.saEnd || defaultSettings.scrollTrigger.end,
                // Set scrub to true only if the data-sa-scrub attribute exists
                scrub: el.hasAttribute("data-sa-scrub"), // Check if data-sa-scrub is present
                markers: el.hasAttribute("data-sa-markers"), // Check if data-sa-scrub is present
                trigger: el,
                toggleActions: el.dataset.saToggleActions || defaultSettings.scrollTrigger.toggleActions,
            };

            // Choose tween method
            const tweenMethod = el.dataset.saTween || defaultTween;

            // Apply animation with or without scrollTrigger
            // gsap[tweenMethod]() = gsap.to() or gsap.from() depending on value of tweenMethod
            gsap[tweenMethod](el, {
                ...animationProps,
                scrollTrigger: scrollTriggerProps, // Always include scrollTrigger props
            });
        });
    }

    initGsapAnimations();

    // Recalculate trigger positions on load since layout shifts (sticky-side resizing, hash scroll, etc.) can invalidate them
    window.addEventListener("load", () => {
        ScrollTrigger.refresh();
    });
}
